/**
 * Inicio: lo que hay que hacer hoy, y el club en cuatro cifras · 13-10-2026
 *
 * La pantalla a la que se llega al entrar en el panel. Hasta hoy era la lista de
 * alumnos, y el primer club echaba en falta dos cosas: **saber qué tenía pendiente
 * sin ir a buscarlo**, y que el móvil no fuera «todo letras, demasiado plano».
 *
 * Tres bloques, en el orden en que se leen —cuatro mientras el club se monta—:
 *
 * 0. **Primeros pasos** (03-11-2026): sede, grupos, horarios, tarifas, alumnos y
 *    ficha, cada uno con su pantalla. Solo al dueño y al administrativo, y solo
 *    mientras falte alguno (`pasos.ts`).
 *
 * 1. **Pendiente**: las mismas tarjetas que la campana, cada una con su botón para ir
 *    a resolverlo. Sin nada, «Todo al día» —que también es una respuesta—.
 * 2. **Tu club hoy**: cuatro cifras que se pulsan y llevan a su pantalla. El dinero,
 *    solo a quien lo ve (`puedeVerCuota`).
 * 3. **Accesos rápidos**: lo que más se hace, en teselas grandes con icono, para no
 *    tener que encontrarlo en un menú.
 */
import { useNavigate } from '@tanstack/react-router';
import { aProblema } from '@/shared/api/errores';
import { useYo } from '@/shared/auth/sesionContexto';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { euros } from '@/shared/ui/formato';
import { Acceso, Cifra, Saludo } from '@/shared/ui/PiezasDeInicio';
import { ListaDePendientes } from '@/shared/ui/pendientes/ListaDePendientes';
import { BotonDelManual } from '@/shared/ui/BotonDelManual';
import { useContextoDelClub } from '@/club/contexto';
import { useHoy } from '@/club/hoy';
import { aPendientes } from '@/club/pendientes';
import { PrimerosPasos } from '@/club/inicio/PrimerosPasos';
import { primerosPasos } from '@/club/inicio/pasos';
import {
  puedeEditarAlumnos,
  puedeEditarElClub,
  puedeGestionarPersonal,
  puedePublicarAvisos,
  puedeVerCuota,
  puedeVerLoPendiente,
} from '@/club/permisos';

export function Inicio() {
  const yo = useYo();
  const { club, rol } = useContextoDelClub();
  /*
   * El entrenador llega aquí si es administrativo en otro club (`permisos.ts`): para
   * él, Inicio son los accesos rápidos. Lo pendiente le respondería `403`.
   */
  const conPendiente = puedeVerLoPendiente(rol);
  const hoy = useHoy(club, conPendiente);
  const navegar = useNavigate();
  const ir = (destino: string) => void navegar({ to: destino });

  const pasos =
    hoy.data && puedeEditarElClub(rol)
      ? primerosPasos(hoy.data.setup, { conTarifas: puedeVerCuota(rol) })
      : [];
  /*
   * «Publica tu ficha» es también un paso: mientras se vea la guía, no se repite
   * como pendiente. Si se oculta la guía, la tarjeta de lo pendiente sigue ahí.
   */
  const fichaEnLaGuia = pasos.some((p) => p.clave === 'ficha' && !p.hecho);
  const pendientes = hoy.data
    ? aPendientes(hoy.data.pending, rol).filter(
        (p) => !(fichaEnLaGuia && p.clave === 'profile_unpublished')
      )
    : [];
  const resumen = hoy.data?.summary;

  return (
    <section className="inicio">
      <Saludo nombre={yo.user.first_name} />

      {pasos.length > 0 && <PrimerosPasos club={club} pasos={pasos} />}

      {conPendiente && (
        <>
          <h2 className="seccion">
            Pendiente
            {pendientes.length > 0 && (
              <span className="seccion-numero">{pendientes.length}</span>
            )}
          </h2>
          {hoy.isPending ? (
            <Cargando filas={3} etiqueta="Mirando lo pendiente" />
          ) : hoy.isError ? (
            <PanelError
              problema={aProblema(hoy.error)}
              reintentar={() => void hoy.refetch()}
            />
          ) : (
            <ListaDePendientes pendientes={pendientes} alIr={ir} />
          )}
        </>
      )}

      {resumen && (
        <>
          <h2 className="seccion">Tu club hoy</h2>
          <div className="cifras">
            <Cifra
              icono="alumnos"
              valor={String(resumen.students_active)}
              etiqueta={
                resumen.students_active === 1
                  ? 'alumno activo'
                  : 'alumnos activos'
              }
              alPulsar={() => ir('/alumnos')}
            />
            <Cifra
              icono="calendario"
              valor={String(resumen.sessions_today)}
              etiqueta={
                resumen.sessions_today === 1 ? 'clase hoy' : 'clases hoy'
              }
              alPulsar={() => ir('/calendario/clases')}
            />
            <Cifra
              icono="avisos"
              valor={`${resumen.guardians_with_app} de ${resumen.guardians}`}
              etiqueta="familias con la app"
              proporcion={
                resumen.guardians > 0
                  ? resumen.guardians_with_app / resumen.guardians
                  : 0
              }
              alPulsar={() => ir('/alumnos')}
            />
            {puedeVerCuota(rol) && (
              <Cifra
                icono="euro"
                valor={euros(resumen.month.paid_cents)}
                etiqueta={`cobrado de ${euros(resumen.month.issued_cents)} este mes`}
                proporcion={
                  resumen.month.issued_cents > 0
                    ? resumen.month.paid_cents / resumen.month.issued_cents
                    : 0
                }
                // Los recibos de este mes, que es de lo que habla la cifra.
                alPulsar={() =>
                  void navegar({
                    to: '/cobros/recibos',
                    search: { periodo: resumen.month.period },
                  })
                }
              />
            )}
          </div>
        </>
      )}

      <h2 className="seccion">Accesos rápidos</h2>
      <div className="accesos-rapidos">
        {puedeEditarAlumnos(rol) && (
          <Acceso
            icono="mas-uno"
            texto="Añadir un alumno"
            alPulsar={() =>
              void navegar({ to: '/alumnos', search: { nuevo: 1 } })
            }
          />
        )}
        {puedePublicarAvisos(rol) && (
          <Acceso
            icono="avisos"
            texto="Escribir un aviso"
            alPulsar={() =>
              void navegar({ to: '/avisos/publicados', search: { nuevo: 1 } })
            }
          />
        )}
        {puedeVerCuota(rol) && (
          <Acceso
            icono="cobros"
            texto="Anotar un cobro"
            alPulsar={() =>
              void navegar({ to: '/cobros/recibos', search: { deudas: 1 } })
            }
          />
        )}
        <Acceso
          icono="calendario"
          texto="Ver el calendario"
          alPulsar={() => ir('/calendario/clases')}
        />
        {puedeGestionarPersonal(rol) && (
          <Acceso
            icono="invitar"
            texto="Invitar a tu equipo"
            alPulsar={() => ir('/club/personal')}
          />
        )}
      </div>

      <p className="inicio-pie">
        ¿Dudas? <BotonDelManual de="club" />
      </p>
    </section>
  );
}
